/**
 * /give/:orderId — collects card and runs the WooPay flow inline.
 *
 * Under the new WooPay flow, 3DS happens via stripe.handleNextAction inline
 * (no redirect-back URL), so this page no longer needs a `?payment_intent`
 * return-path branch. It just renders the inline card form and navigates to
 * the success page when finalize confirms the order is paid.
 *
 * The donor / recognition / amount context is stashed in sessionStorage by
 * the upstream WishCheckout / GeneralCheckout pages so a refresh on this
 * page can rehydrate without a re-collection.
 */
import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";
import { EmbeddedPaymentForm } from "@/components/give/EmbeddedPaymentForm";
import { type DonorPayload, type Recognition } from "@/services/give-api";

interface CheckoutContext {
  donor: DonorPayload;
  recognition: Recognition;
  amountUsd: number;
  wishItemName?: string;
}

export function Pay() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [context, setContext] = React.useState<CheckoutContext | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!orderId) return;
    const raw = sessionStorage.getItem(`give-checkout-${orderId}`);
    if (raw) {
      try {
        setContext(JSON.parse(raw));
      } catch {
        setError("Could not read checkout context — please start over.");
      }
    } else {
      setError("Checkout context missing. Please start over from the wish item.");
    }
  }, [orderId]);

  function onSucceeded(intentId: string) {
    sessionStorage.removeItem(`give-checkout-${orderId}`);
    navigate(`/give/${orderId}/success?intent=${encodeURIComponent(intentId)}`, { replace: true });
  }

  return (
    <PageShell
      title={`Donation #${orderId ?? ""}`}
      subtitle={
        context?.wishItemName
          ? `Directed gift — ${context.wishItemName}`
          : "Card payment to the .person Foundation"
      }
      stewardship="Give · payment"
    >
      <Seo
        title={`Order ${orderId ?? ""}`}
        description=""
        path={`/give/${orderId ?? ""}`}
        noindex
      />
      <div className="max-w-2xl">
        <Link
          to="/foundation/contribute"
          className="font-mono text-[10px] uppercase tracking-widest text-ink-3 hover:text-ink mb-8 inline-block"
        >
          ← Back to Contribute
        </Link>
        {context && (
          <div className="border border-hairline bg-paper-2 p-4 mb-6 font-mono text-xs text-ink-2 leading-relaxed">
            Total:{" "}
            <strong className="text-ink">
              US${context.amountUsd.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </strong>
            {context.wishItemName ? <> · Memo: {context.wishItemName}</> : null}
          </div>
        )}
        {error && (
          <div className="border border-state-pending bg-paper-2 p-4 font-mono text-xs text-state-pending mb-6">
            {error}
          </div>
        )}
        {context && orderId && (
          <EmbeddedPaymentForm
            orderId={Number(orderId)}
            donor={context.donor}
            recognition={context.recognition}
            amountUsd={context.amountUsd}
            onSucceeded={onSucceeded}
          />
        )}
        {!context && !error && (
          <p className="font-mono text-xs text-ink-3">Loading…</p>
        )}
      </div>
    </PageShell>
  );
}
